import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, CheckCircle2, AlertTriangle, MinusCircle, FileSpreadsheet } from 'lucide-react';

const fmt = (v) => (v === null || v === undefined)
  ? '—'
  : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

const STATUS = {
  match: { label: 'Match', cls: 'text-emerald-500', Icon: CheckCircle2 },
  mismatch: { label: 'Mismatch', cls: 'text-rose-500', Icon: AlertTriangle },
  unmapped: { label: 'Not in report', cls: 'text-muted-foreground', Icon: MinusCircle },
  not_in_report: { label: 'Not in report', cls: 'text-muted-foreground', Icon: MinusCircle },
};

export function ReportSourceComparison({ reportId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !reportId) return;
    setLoading(true);
    setData(null);
    api.getReportSourceComparison(reportId)
      .then(setData)
      .catch((e) => setData({ available: false, reason: e.message || 'Could not load comparison' }))
      .finally(() => setLoading(false));
  }, [open, reportId]);

  const rows = data?.rows || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="source-comparison-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" /> Source file vs report
          </DialogTitle>
          <DialogDescription>
            {data?.available
              ? `Comparing column grand totals from ${data.file_name} (${fmt(data.row_count)} rows) against what this report shows.`
              : 'Reconcile the uploaded spreadsheet against the generated report.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="source-comparison-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Reconciling values…
          </div>
        ) : !data?.available ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="source-comparison-unavailable">
            {data?.reason || 'Comparison is not available for this report.'}
          </div>
        ) : (
          <div data-testid="source-comparison-content">
            <div className="flex gap-2 mb-4 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium" data-testid="source-comparison-match-count">{data.summary.match} match</span>
              <span className="px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 font-medium" data-testid="source-comparison-mismatch-count">{data.summary.mismatch} mismatch</span>
              <span className="px-2.5 py-1 rounded-full bg-muted text-muted-foreground font-medium">{data.summary.unmapped} unmapped</span>
            </div>
            <div className="border border-border/50 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Field / column</th>
                    <th className="px-3 py-2 font-medium text-right">Source total</th>
                    <th className="px-3 py-2 font-medium text-right">Report total</th>
                    <th className="px-3 py-2 font-medium text-right">Difference</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const s = STATUS[r.status] || STATUS.unmapped;
                    const rowCls = r.status === 'mismatch' ? 'bg-rose-500/5' : r.status === 'match' ? 'bg-emerald-500/5' : '';
                    return (
                      <tr key={i} className={`border-t border-border/40 ${rowCls}`} data-testid={`source-comparison-row-${i}`}>
                        <td className="px-3 py-2 font-medium text-foreground">
                          {r.label}
                          {r.source_column && r.source_column !== r.label && (
                            <span className="block text-[10px] text-muted-foreground">↔ {r.source_column}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(r.source_value)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(r.report_value)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${r.status === 'mismatch' ? 'text-rose-500 font-medium' : 'text-muted-foreground'}`}>{r.delta === null || r.delta === undefined ? '—' : fmt(r.delta)}</td>
                        <td className={`px-3 py-2 ${s.cls}`}>
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium"><s.Icon className="w-3.5 h-3.5" /> {s.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground text-sm">No comparable numeric columns were found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              Totals within 0.1% are treated as a match. Differences can be legitimate (filters, data-model transforms) — use this to spot unexpected gaps.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
